import Box, { type BoxProps } from '@mui/material/Box';
import Slide, { type SlideProps } from '@mui/material/Slide';
import Stack, { type StackProps } from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { type PropsWithChildren, useCallback } from 'react';
import { useReportSaveBarRef } from './presence-context';

type SaveBarContainerProps = PropsWithChildren<{
  slideProps?: Partial<SlideProps>;
  containerProps?: Partial<BoxProps>;
  contentProps?: Partial<StackProps>;
}>;

export const SaveBarContainer = ({
  children,
  containerProps,
  contentProps,
  slideProps,
}: SaveBarContainerProps) => {
  const { zIndex } = useTheme();
  const reportSaveBarRef = useReportSaveBarRef();

  // Clear the inline transform style that MUI Slide leaves after animation.
  // The lingering `transform: none` on a position:fixed element can cause
  // stale compositor hit-testing in some browser/GPU combinations, making
  // the save bar unclickable until a layout reflow (e.g. opening dev tools).
  const handleEntered = useCallback(
    (node: HTMLElement, isAppearing: boolean) => {
      node.style.webkitTransform = '';
      node.style.transform = '';
      slideProps?.onEntered?.(node, isAppearing);
    },
    [slideProps?.onEntered],
  );

  const content = (
    <Box
      ref={reportSaveBarRef}
      className="bulk-edit-save-bar"
      position="fixed"
      bottom={0}
      left={0}
      display="flex"
      bgcolor="slate.100"
      width="100%"
      py={1.25}
      px={2.25}
      zIndex={zIndex.drawer + 1}
      boxShadow="0px -1px 10px rgba(99, 102, 241, 0.20)"
      {...containerProps}
      sx={{
        willChange: 'transform',
        borderTopRightRadius: 24,
        borderTopLeftRadius: 24,
        borderTop: '1px solid',
        borderTopColor: 'indigo.50',
        ...containerProps?.sx,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        bgcolor="background.paper"
        border="1px solid"
        borderColor="indigo.50"
        borderRadius={1.5}
        {...contentProps}
      >
        {children}
      </Stack>
    </Box>
  );

  if (!slideProps) {
    return content;
  }

  return (
    <Slide
      direction="up"
      mountOnEnter
      unmountOnExit
      {...slideProps}
      onEntered={handleEntered}
    >
      {content}
    </Slide>
  );
};
